import React, { useState } from "react";
import axios from "axios";
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalBody,
} from "@chakra-ui/modal";
import Card from "components/card";
import { Button } from '@chakra-ui/react';
import DisplayAmount from "../../shared/DisplayAmount";
import { TokenManager } from "utils/tokenManager";

const BatchCancelConfirmModal: React.FC<{
    batchToCancel: any;
    isOpen: boolean;
    onClose(): void;
    triggerReload(): void;
    onProcessingStarted?(batchId: string, optimisticStatus?: string): void;
    setSwalProps: any;
}> = ({ isOpen, onClose, triggerReload, onProcessingStarted, batchToCancel, setSwalProps }) => {

    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const userRole = localStorage.getItem("userRole")?.toLowerCase();

    const closeModal = async () => {
        onClose();
    };

    const handleSubmit = async () => {
        try {
            setIsProcessing(true);

            const data = {
                batchid: batchToCancel.id,
                username: localStorage.getItem("username"),
                userRole: userRole,
                subaccount_id: batchToCancel.subaccount.id
            }

            await axios.post(process.env.REACT_APP_API_URL + `/api/${userRole}/batch/action/cancel-batch`, data)
                .then(function (response) {
                    if (response && response.data?.status === 'ok') {
                        onClose();
                        if (onProcessingStarted) {
                            onProcessingStarted(batchToCancel.id, "cancelling");
                        }
                        setSwalProps({
                            show: true,
                            icon: "success",
                            title: "Success",
                            html: "Batch is being cancelled",
                            showConfirmButton: true,
                            didClose: () => {
                                setSwalProps({});
                                setIsProcessing(false);
                                if (!onProcessingStarted) {
                                    triggerReload();
                                }
                            },
                        });
                    } else {
                        setIsProcessing(false);
                    }
                })
                .catch(function (error) {
                    setIsProcessing(false);
                    setSwalProps({
                        show: true,
                        icon: "error",
                        title: "Oops!",
                        html: error.response.data.message,
                        showConfirmButton: true,
                        didClose: () => {
                            setSwalProps({});
                            setIsProcessing(false);
                        },
                    });
                });
        } catch (error: any) {
            setIsProcessing(false);
            setSwalProps({
                show: true,
                icon: "error",
                title: "Oops!",
                html: "Something went wrong, please try again later.",
                showConfirmButton: true,
                didClose: () => {
                    setSwalProps({});
                    setIsProcessing(false);
                },
            });
        }
    };

    return (
        <>
            {batchToCancel &&
                <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
                    <ModalOverlay className="bg-[#000] !opacity-30" />
                    <ModalContent className="dark:!bg-neutral-900 !z-[1002] !m-auto !w-full max-w-2xl !rounded-2xl !border !border-gray-200 !bg-white !shadow-xl dark:!border-white/10 md:!top-[12vh]">
                        <ModalBody className="p-6 md:p-7">
                            <div className="mb-4 flex items-start gap-3">
                                <div className="rounded-xl bg-red-50 p-2 text-red-600 ring-1 ring-red-100 dark:bg-red-900/20 dark:text-red-400 dark:ring-red-900/30">
                                    <svg
                                        viewBox="0 0 24 24"
                                        className="h-5 w-5"
                                        fill="currentColor"
                                    >
                                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                                    </svg>
                                </div>
                                <div>
                                    <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                                        Cancel Batch
                                    </h1>
                                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                                        This action cannot be undone
                                    </p>
                                </div>
                            </div>

                            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300">
                                You're about to cancel the current batch. This action will discard all associated payouts, and the transactions will not be processed. This action cannot be undone.
                            </div>

                            <div className="dark:bg-neutral-800/50 mb-6 rounded-xl bg-gray-50 p-4 ring-1 ring-gray-100 dark:ring-white/10">
                                <div className="flex flex-col gap-3 text-sm">
                                    {userRole === 'superadmin' &&
                                        batchToCancel.provider_name &&
                                        batchToCancel.provider_account_name &&
                                        batchToCancel.provider_method_name && (
                                            <>
                                                <div className="flex">
                                                    <div className="w-40 shrink-0 text-gray-700 dark:text-gray-400">
                                                        Provider
                                                    </div>
                                                    <div className="font-medium text-gray-900 dark:text-white">
                                                        {batchToCancel.provider_name} ({batchToCancel.provider_account_name})
                                                    </div>
                                                </div>
                                                <div className="flex">
                                                    <div className="w-40 shrink-0 text-gray-700 dark:text-gray-400">
                                                        Provider Method
                                                    </div>
                                                    <div className="font-medium text-gray-900 dark:text-white">
                                                        {batchToCancel.provider_method_name}
                                                    </div>
                                                </div>
                                            </>
                                        )
                                    }

                                    {(userRole === 'superadmin' || userRole === 'merchant') && batchToCancel.subaccount?.name && (
                                        <div className="flex">
                                            <div className="w-40 shrink-0 text-gray-700 dark:text-gray-400">
                                                Subaccount
                                            </div>
                                            <div className="font-medium text-gray-900 dark:text-white">
                                                {batchToCancel.subaccount.name}
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex">
                                        <div className="w-40 shrink-0 text-gray-700 dark:text-gray-400">
                                            Record(s)
                                        </div>
                                        <div className="font-medium text-gray-900 dark:text-white">
                                            {batchToCancel.record}
                                        </div>
                                    </div>

                                    <div className="flex">
                                        <div className="w-40 shrink-0 text-gray-700 dark:text-gray-400">
                                            Amount
                                        </div>
                                        <div className="font-medium text-gray-900 dark:text-white">
                                            {batchToCancel.crypto ? batchToCancel.amount : <DisplayAmount amount={Number(batchToCancel.amount)} currency={batchToCancel.asset} />} {batchToCancel.asset}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-end gap-2">
                                <Button
                                    onClick={() => closeModal()}
                                    isDisabled={isProcessing}
                                    className="dark:bg-neutral-900 dark:hover:bg-neutral-800 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-white/10 dark:text-white"
                                >
                                    Not now
                                </Button>

                                <Button
                                    onClick={() => handleSubmit()}
                                    isDisabled={isProcessing || TokenManager.isImpersonating()}
                                    isLoading={isProcessing}
                                    loadingText='Processing...'
                                    className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/30 disabled:cursor-not-allowed disabled:bg-gray-400"
                                >
                                    Cancel batch
                                </Button>
                            </div>
                        </ModalBody>
                    </ModalContent>
                </Modal>
            }
        </>
    );
};

export default BatchCancelConfirmModal;